/**
 * Domain utility functions for news articles.
 * Pure functions with zero external dependencies to maintain CRAP <= 8 and 100% testability.
 */

/**
 * Generates a clean URL slug from Norwegian titles.
 * Replaces æ/ø/å with ae/oe/aa, trims whitespace, and collapses hyphens.
 */
export const generateSlug = (title) => {
    if (!title || typeof title !== "string") return "";

    return title
        .toLowerCase()
        .replace(/æ/g, "ae")
        .replace(/ø/g, "oe")
        .replace(/å/g, "aa")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
};

/**
 * Calculates estimated reading time in minutes based on average reading speed (200 wpm).
 * Returns e.g. "2 min lesetid".
 */
export const calculateReadingTime = (htmlOrText) => {
    if (!htmlOrText || typeof htmlOrText !== "string") {
        return "1 min lesetid";
    }

    const clean = stripHtml(htmlOrText);
    const words = clean.trim().split(/\s+/).filter(Boolean).length;
    const minutes = Math.max(1, Math.ceil(words / 200));

    return `${minutes} min lesetid`;
};

/**
 * Strips HTML tags and resolves common HTML entities.
 */
export const stripHtml = (html) => {
    if (!html || typeof html !== "string") return "";

    return html
        .replace(/<br\s*\/?>/gi, " ")
        .replace(/<\/p>/gi, " ")
        .replace(/<[^>]+>/g, "")
        .replace(/&nbsp;/g, " ")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/\s+/g, " ")
        .trim();
};

/**
 * Formats publication date in Norwegian locale.
 */
export const formatArticleDate = (dateVal) => {
    if (!dateVal) return "";
    let dateObj;

    if (typeof dateVal.toDate === "function") {
        dateObj = dateVal.toDate();
    } else if (dateVal instanceof Date) {
        dateObj = dateVal;
    } else if (typeof dateVal === "string" || typeof dateVal === "number") {
        dateObj = new Date(dateVal);
    }

    if (!dateObj || isNaN(dateObj.getTime())) return "";

    try {
        return dateObj.toLocaleDateString("no-NO", {
            day: "numeric",
            month: "long",
            year: "numeric",
        });
    } catch {
        return "";
    }
};

/**
 * Validates whether a URL is safe for navigation or anchor hrefs.
 * Only allows relative paths (/...), https://, http://, and mailto:.
 */
export const isSafeUrl = (url) => {
    if (!url || typeof url !== "string") return false;
    const trimmed = url.trim();
    if (trimmed.startsWith("/")) return true;
    if (trimmed.startsWith("https://") || trimmed.startsWith("http://")) return true;
    if (trimmed.startsWith("mailto:")) return true;
    return false;
};
